import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IntegrationApi from "../../apis/integrationApi";
import { m365SetupInstructions } from "../../utils/constants";
import M365ConnectionChecksResultsBox, {
  type M365ConnectionCheck,
} from "./M365ConnectionChecksResultsBox";

export interface M365PostSaveGrantPermissionsPanelProps {
  customerId: string;
}

/**
 * Post-save M365: step 5 (grant API permissions) + Test and enable M365 actions.
 * Shared by AddIntegrationModal and OnboardingWizard.
 */
const M365PostSaveGrantPermissionsPanel: React.FC<
  M365PostSaveGrantPermissionsPanelProps
> = ({ customerId }) => {
  const integrationApi = useMemo(() => new IntegrationApi(), []);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(
    null
  );
  const [fullConnectionCheck, setFullConnectionCheck] = useState<{
    success: boolean;
    message: string;
    checks?: M365ConnectionCheck[];
  } | null>(null);

  const handleTestAndEnable = async () => {
    if (!customerId) return;
    setLoading(true);
    setResult(null);
    setFullConnectionCheck(null);
    try {
      const res = (await integrationApi.testAndEnableM365Actions(customerId)) as {
        message?: string;
        error?: string;
      };
      if (res?.error) {
        setResult({ success: false, message: res.error });
        return;
      }
      setResult({
        success: true,
        message: res?.message || "All M365 permissions granted successfully.",
      });

      try {
        const full = (await integrationApi.testApplicationConfiguration(
          customerId,
          "m365",
          {}
        )) as {
          success?: boolean;
          message?: string;
          checks?: M365ConnectionCheck[];
        };
        const checks = Array.isArray(full?.checks) ? full.checks : undefined;
        if (full?.success) {
          setFullConnectionCheck({
            success: true,
            message:
              full.message ||
              "Full M365 connection check passed (same as “Test M365 Credentials and Permissions” in App Configuration).",
            checks,
          });
        } else {
          setFullConnectionCheck({
            success: false,
            message:
              full?.message ||
              "Full M365 connection check failed. Fix items in Microsoft Entra or use Test M365 Credentials and Permissions on App Configuration.",
            checks,
          });
        }
      } catch (fe: unknown) {
        const feAny = fe as {
          response?: { data?: { details?: string; error?: string } };
          message?: string;
        };
        setFullConnectionCheck({
          success: false,
          message:
            feAny?.response?.data?.details ||
            feAny?.response?.data?.error ||
            feAny?.message ||
            "Full M365 connection check failed.",
        });
      }
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
      setResult({
        success: false,
        message:
          anyErr?.response?.data?.error ||
          anyErr?.message ||
          "Failed to test and enable M365 actions.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert severity="success" sx={{ mb: 2, fontFamily: "var(--theo-font-sans)" }}>
        M365 configuration saved successfully. Complete the steps below to grant API
        permissions in Microsoft Entra.
      </Alert>
      <Box sx={{ p: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
        {m365SetupInstructions
          .filter((s) => s.step === 5)
          .map(({ step, title, steps: stepItems }) => (
            <Box key={step}>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#1a1a1a",
                  mb: 1.5,
                }}
              >
                {step}. {title}
              </Typography>
              <Box sx={{ m: 0, pl: 2.5 }}>
                {stepItems.map((s, i) => {
                  if (typeof s === "object" && "heading" in s) {
                    return (
                      <Typography
                        key={i}
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 14,
                          color: "#555",
                          lineHeight: 1.5,
                          mb: 1,
                        }}
                      >
                        {s.heading}
                      </Typography>
                    );
                  }
                  if (typeof s === "object" && "copyableBullets" in s) {
                    return null;
                  }
                  if (typeof s === "object" && "numbered" in s) {
                    const numberedItems = stepItems.filter(
                      (x): x is { numbered: string } =>
                        typeof x === "object" && "numbered" in x
                    );
                    const copyableIdx = stepItems.findIndex(
                      (x) => typeof x === "object" && "copyableBullets" in x
                    );
                    const copyableItem =
                      copyableIdx >= 0 ? stepItems[copyableIdx] : undefined;
                    const copyableBlock: string[] | null =
                      copyableItem &&
                      typeof copyableItem === "object" &&
                      "copyableBullets" in copyableItem
                        ? (copyableItem as { copyableBullets: string[] }).copyableBullets
                        : null;
                    const idx = numberedItems.indexOf(s);
                    if (idx > 0) return null;
                    return (
                      <Box
                        key={i}
                        component="ol"
                        sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}
                      >
                        {numberedItems.map((item, j) => (
                          <Box
                            key={j}
                            component="li"
                            sx={{
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: 14,
                              color: "#555",
                              lineHeight: 1.5,
                              mb: 0.75,
                            }}
                          >
                            {item.numbered}
                            {copyableBlock && j === 1 && (
                              <Box
                                component="ul"
                                sx={{
                                  m: 0.5,
                                  pl: 2.5,
                                  "& li": {
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  },
                                }}
                              >
                                {copyableBlock.map((text: string, k: number) => (
                                  <Typography
                                    key={k}
                                    component="li"
                                    sx={{
                                      fontFamily: "var(--theo-font-sans)",
                                      fontSize: 14,
                                      color: "#555",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {text}
                                    <IconButton
                                      size="small"
                                      onClick={() => navigator.clipboard.writeText(text)}
                                      sx={{ color: "#705d4a", p: 0.25, ml: 0.5 }}
                                      title="Copy"
                                      aria-label={`Copy ${text}`}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                  const bulletItems = stepItems.filter(
                    (x) => typeof x === "string" || (typeof x === "object" && "before" in x)
                  );
                  const bulletIdx = bulletItems.findIndex(
                    (x) =>
                      x === s ||
                      (typeof s === "object" &&
                        "before" in s &&
                        typeof x === "object" &&
                        "before" in x &&
                        x.before === (s as { before: string }).before)
                  );
                  if (bulletIdx > 0) return null;
                  if (bulletItems.length > 0) {
                    return (
                      <Box
                        key={i}
                        component="ul"
                        sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}
                      >
                        {bulletItems.map((item, j) => (
                          <Typography
                            key={j}
                            component="li"
                            sx={{
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: 14,
                              color: "#555",
                              lineHeight: 1.5,
                            }}
                          >
                            {typeof item === "string" ? (
                              item
                            ) : "before" in item ? (
                              <>
                                {item.before}
                                <Link
                                  href={item.linkUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: "#705d4a", fontWeight: 500 }}
                                >
                                  {item.linkText}
                                </Link>
                                {item.after}
                              </>
                            ) : null}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
            </Box>
          ))}
      </Box>
      <Box
        sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Button
          variant="contained"
          onClick={handleTestAndEnable}
          disabled={loading}
          sx={{
            backgroundColor: "#705d4a",
            "&:hover": { backgroundColor: "#5a4a3a" },
            fontFamily: "var(--theo-font-sans)",
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
              Granting permissions ...
            </>
          ) : (
            "Test and enable M365 actions"
          )}
        </Button>
        {result && (
          <Alert
            severity={result.success ? "success" : "error"}
            sx={{
              flex: 1,
              minWidth: 200,
              fontFamily: "var(--theo-font-sans)",
              whiteSpace: "pre-line",
            }}
          >
            {result.message}
          </Alert>
        )}
      </Box>
      {fullConnectionCheck?.checks && fullConnectionCheck.checks.length > 0 ? (
        <M365ConnectionChecksResultsBox
          checks={fullConnectionCheck.checks}
          sx={{ mt: 2, width: "100%" }}
        />
      ) : fullConnectionCheck ? (
        <Alert
          severity={fullConnectionCheck.success ? "success" : "warning"}
          sx={{
            mt: 2,
            width: "100%",
            fontFamily: "var(--theo-font-sans)",
            whiteSpace: "pre-line",
          }}
        >
          <Typography component="span" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Full connection check
          </Typography>
          {fullConnectionCheck.message}
        </Alert>
      ) : null}
    </>
  );
};

export default M365PostSaveGrantPermissionsPanel;
